import React, { useEffect, useState, useRef } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Card,
  CardMedia,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {useToast} from '../../components/ToastProvider/ToastProvider';
import { 
  getUploadLog,
  getCatalogImpact,
  getStagedItems, 
  updateStagedItem, 
  importStaged, 
  runAIInference,
  startAIInference,
  getAIJobStatus,
  cancelAIJob,
  startImageExtraction,
  getImageJobStatus,
  cancelImageJob,
  bulkExtractImages,
  getExtractedImages,
  selectProductImages,
  extractImagesForProduct,
  deleteStagedProductImage
} from '../../api/api';
import ImageSearch from '../ImageSearch/ImageSearch';
import ImageUploader from '../ImageUploader/ImageUploader';

export default function ReviewStagedPanel({ logId, readOnly, onClose, onChangesDetected }) {
  const { showToast } = useToast();
  const [log, setLog] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [aiRunning, setAiRunning] = useState(false);
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [aiJobId, setAiJobId] = useState(null);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiStatus, setAiStatus] = useState('');
  const [aiCancelling, setAiCancelling] = useState(false);
  const [imageJobId, setImageJobId] = useState(null);
  const [imageProgress, setImageProgress] = useState(0);
  const [imageStatus, setImageStatus] = useState('');
  const [imageCancelling, setImageCancelling] = useState(false);
  const aiPollIntervalRef = useRef(null);
  const imagePollIntervalRef = useRef(null);
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(null);
  const [extractingImages, setExtractingImages] = useState(false);
  const [extractedImages, setExtractedImages] = useState({}); // productId -> images array
  const [selectedImages, setSelectedImages] = useState({}); // productId -> selected image indices
  const [uploadedImages, setUploadedImages] = useState({}); // productId -> uploaded image indices
  const [uploadedCustomImages, setUploadedCustomImages] = useState({}); // productId -> uploaded custom image indices
  const [individualAIStatus, setIndividualAIStatus] = useState({}); // productId -> AI status
  const [individualExtractStatus, setIndividualExtractStatus] = useState({}); // productId -> extract status
  const [aiBatches, setAiBatches] = useState([]); // Store batches for AI processing
  const [imageBatches, setImageBatches] = useState([]); // Store batches for image extraction
  const [currentAiBatch, setCurrentAiBatch] = useState(0); // Current batch index for AI
  const [currentImageBatch, setCurrentImageBatch] = useState(0); // Current batch index for images
  const [aiBatchCancelled, setAiBatchCancelled] = useState(false); // Track if AI batches were cancelled
  const [imageBatchCancelled, setImageBatchCancelled] = useState(false); // Track if image batches were cancelled
  
  // Filter state
  const [productFilter, setProductFilter] = useState('all'); // 'all', 'new', 'updated', 'no-change'
  const [filteredItems, setFilteredItems] = useState([]);
  const [catalogImpact, setCatalogImpact] = useState(null);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  
  const pageSize = 20;
  const BATCH_SIZE = 10; // Process 10 products per batch

  // Helper function to split array into batches
  const splitIntoBatches = (array, batchSize) => {
    const batches = [];
    for (let i = 0; i < array.length; i += batchSize) {
      batches.push(array.slice(i, i + batchSize));
    }
    return batches;
  };

  const isComplete = log?.status === 'complete';

  const filterProducts = (products, filter) => {
    if (filter === 'all') {
      return products;
    }

    return products.filter(product => {
      const isExisting = product.is_existing === true;
      const hasChanges = product.has_changes === true;
      const hasStockChange = product.has_stock_change === true;
      const hasPriceChange = product.has_price_change === true;

      switch (filter) {
        case 'new':
          return !isExisting;
        case 'price-and-stock':
          return isExisting && hasPriceChange && hasStockChange;
        case 'price-only':
          return isExisting && hasPriceChange && !hasStockChange;
        case 'stock-only':
          return isExisting && hasStockChange && !hasPriceChange;
        case 'other':
          return isExisting && hasChanges && !hasPriceChange && !hasStockChange;
        case 'no-change':
          return isExisting && !hasChanges;
        default:
          return true;
      }
    });
  };

  // Update filtered items when filter or items change
  useEffect(() => {
    const filtered = filterProducts(items, productFilter);
    setFilteredItems(filtered);
    
  }, [items, productFilter]);

  async function load() {
    setBusy(true);
    try {
      // First, get the upload log to check status
      const l = await getUploadLog(logId);
      setLog(l);

      if ((l.catalog_upload_mode || 'partial') === 'full_replace') {
        try {
          const imp = await getCatalogImpact(logId);
          setCatalogImpact(imp);
        } catch {
          setCatalogImpact(null);
        }
      } else {
        setCatalogImpact(null);
      }

      // If log status is 'processing' or 'staging', wait and retry
      if (l.status === 'processing' || l.status === 'staging') {
        console.log('Upload still processing, waiting...');
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        // Retry loading after delay
        setTimeout(() => load(), 1000);
        return;
      }
      
      // Try to load staged items with retry logic
      let retryCount = 0;
      const maxRetries = 5;
      
      while (retryCount < maxRetries) {
        try {
          const { items: rows, total: t } = await getStagedItems(logId, { skip: page * pageSize, limit: pageSize });
          
          if (rows && rows.length > 0) {
            setItems(rows);
            setTotal(t);
            const calculatedTotalPages = Math.max(1, Math.ceil(t / pageSize));
            setTotalPages(calculatedTotalPages);
            console.log(`Successfully loaded ${rows.length} items (${t} total)`);
            break;
          } else if (retryCount < maxRetries - 1) {
            console.log(`No items found, retrying... (${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 2000));
            retryCount++;
          } else {
            console.log('No items found after all retries');
            setItems([]);
            setTotal(0);
            setTotalPages(1);
          }
        } catch (error) {
          if (retryCount < maxRetries - 1) {
            console.log(`Error loading items, retrying... (${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 2000));
            retryCount++;
          } else {
            throw error;
          }
        }
      }
      
    } catch (e) {
      console.error('Failed to load data:', e);
      showToast(e.message || 'Failed to load log', 'error');
      setItems([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setBusy(false);
    }
  }

  async function loadExtractedImages(products) {
    const newExtractedImages = {};
    for (const product of products) {
      try {
        const response = await getExtractedImages(product.id);
        if (response.images && response.images.length > 0) {
          newExtractedImages[product.id] = response.images;
        }
      } catch (e) {
        console.log(`No extracted images for product ${product.id}`);
      }
    }
    setExtractedImages(newExtractedImages);
  }

  // Lazy load images for a specific product
  async function loadImagesForProduct(productId) {
    if (extractedImages[productId]) {
      return; // Already loaded
    }
    
    try {
      const response = await getExtractedImages(productId);
      if (response.images && response.images.length > 0) {
        setExtractedImages(prev => ({
          ...prev,
          [productId]: response.images
        }));
        console.log(`Lazy loaded ${response.images.length} images for product ${productId}`);
      }
    } catch (e) {
      console.log(`No extracted images for product ${productId}`);
    }
  }

  useEffect(() => { load(); }, [logId, page]);

  async function saveRow(id, partial) {
    try {
      setSavingId(id);
      const updated = await updateStagedItem(logId, id, partial);
      setItems(prev => prev.map(r => (r.id === id ? { ...r, ...updated } : r)));
      
      // Notify parent component that changes have been made
      if (onChangesDetected) {
        onChangesDetected(true);
      }
    } catch (e) {
      showToast(e.message || 'Failed to save row', 'error');
    } finally {
      setSavingId(null);
    }
  }

  async function runImport() {
    setImportConfirmOpen(false);
    try {
      setBusy(true);
      const res = await importStaged(logId);
      await load();
      const ok = res.successful_imports ?? res.successful;
      const fail = res.failed_imports ?? res.failed;
      showToast(`Imported: ${ok}/${res.total_rows}. ${fail ? `${fail} failed — fix & re-import.` : 'All done!'}`, 'info');
      if (!fail && onClose) onClose();
    } catch (e) {
      showToast(e.message || 'Import failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleImportClick() {
    if ((log?.catalog_upload_mode || 'partial') === 'full_replace') {
      try {
        const imp = await getCatalogImpact(logId);
        setCatalogImpact(imp);
      } catch {
        setCatalogImpact(null);
      }
      setImportConfirmOpen(true);
      return;
    }
    await runImport();
  }

  async function doBulkExtractImages() {
    try {
      setExtractingImages(true);
      setImageProgress(0);
      setImageStatus('Starting image extraction...');
      setImageBatchCancelled(false);
      setCurrentImageBatch(0);
      
      // Filter out products that already have images
      const productsWithoutImages = items.filter(item => 
        !item.image_urls || item.image_urls.length === 0
      );
      
      if (productsWithoutImages.length === 0) {
        showToast('All products already have images. No extraction needed.', 'info');
        setExtractingImages(false);
        return;
      }
      
      if (productsWithoutImages.length < items.length) {
        const skippedCount = items.length - productsWithoutImages.length;
        showToast(`Skipping ${skippedCount} products that already have images. Extracting for ${productsWithoutImages.length} products in batches of ${BATCH_SIZE}.`, 'info');
      }
      
      // Split products into batches of 10
      const batches = splitIntoBatches(productsWithoutImages, BATCH_SIZE);
      setImageBatches(batches);
      
      // Start processing batches sequentially
      processImageBatches(batches, 0);
      
    } catch (e) {
      showToast(e.message || 'Failed to start image extraction', 'error');
      setExtractingImages(false);
    }
  }

  async function processImageBatches(batches, batchIndex) {
    if (batchIndex >= batches.length || imageBatchCancelled) {
      // All batches completed or cancelled
      setExtractingImages(false);
      if (!imageBatchCancelled) {
        setImageProgress(100);
        setImageStatus('All batches completed');
        await load(); // Reload to get updated data
        showToast(`Image Extraction Complete: All ${batches.length} batch(es) processed successfully.`, 'success');
        
        // Reset after 3 seconds
        setTimeout(() => {
          setImageStatus('');
          setImageProgress(0);
          setImageBatches([]);
          setCurrentImageBatch(0);
        }, 3000);
      }
      return;
    }

    const currentBatch = batches[batchIndex];
    const totalBatches = batches.length;
    const batchNumber = batchIndex + 1;
    
    setCurrentImageBatch(batchIndex);
    setImageStatus(`Processing batch ${batchNumber}/${totalBatches} (${currentBatch.length} products)...`);
    
    // Calculate progress: (completed batches / total batches) * 100
    const progressPercent = Math.round((batchIndex / totalBatches) * 100);
    setImageProgress(progressPercent);

    try {
      // Get product IDs for current batch
      const productIds = currentBatch.map(item => item.id);
      
      // Start image extraction for this batch
      const response = await startImageExtraction(logId, productIds);
      const jobId = response.job_id;
      setImageJobId(jobId);
      
      // Poll for this batch's completion
      await pollImageBatchUntilComplete(jobId, batchIndex, totalBatches);
      
      // Process next batch
      if (!imageBatchCancelled) {
        processImageBatches(batches, batchIndex + 1);
      }
      
    } catch (e) {
      console.error(`Error processing image batch ${batchNumber}:`, e);
      showToast(`Batch ${batchNumber} failed: ${e.message || 'Unknown error'}`, 'error');
      
      // Continue with next batch even if current batch failed
      if (!imageBatchCancelled) {
        processImageBatches(batches, batchIndex + 1);
      }
    }
  }

  async function pollImageBatchUntilComplete(jobId, batchIndex, totalBatches) {
    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          if (imageBatchCancelled) {
            clearInterval(pollInterval);
            // Try to cancel the job
            try {
              await cancelImageJob(jobId);
            } catch (e) {
              console.error('Error cancelling image job:', e);
            }
            reject(new Error('Batch processing cancelled'));
            return;
          }

          const status = await getImageJobStatus(jobId);
          
          // Update progress based on current batch
          const processed = status.processed || 0;
          const total = status.total_products || 0;
          const batchProgress = total > 0 ? Math.round((processed / total) * 100) : 0;
          
          // Overall progress: (completed batches + current batch progress) / total batches
          const overallProgress = Math.round(((batchIndex + (batchProgress / 100)) / totalBatches) * 100);
          setImageProgress(overallProgress);
          
          setImageStatus(`Processing batch ${batchIndex + 1}/${totalBatches}... ${processed}/${total} products (${batchProgress}%)`);

          if (status.status === 'completed') {
            clearInterval(pollInterval);
            resolve(status);
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            const errorMsg = status.errors?.join(', ') || 'Unknown error';
            console.error(`Batch ${batchIndex + 1} failed:`, errorMsg);
            reject(new Error(errorMsg));
          }
        } catch (e) {
          console.error('Error polling image batch status:', e);
          clearInterval(pollInterval);
          reject(e);
        }
      }, 2000); // Poll every 2 seconds
    });
  }

  async function cancelImageExtraction() {
    if (imageCancelling) return; // Prevent multiple clicks
    
    try {
      setImageCancelling(true);
      setImageBatchCancelled(true);
      
      // Cancel current job if exists
      if (imageJobId) {
        try {
          await cancelImageJob(imageJobId);
        } catch (e) {
          console.error('Error cancelling image job:', e);
        }
      }
      
      // Clear the polling interval
      if (imagePollIntervalRef.current) {
        clearInterval(imagePollIntervalRef.current);
        imagePollIntervalRef.current = null;
      }
      
      setExtractingImages(false);
      setImageJobId(null);
      setImageProgress(0);
      setImageStatus('Cancelled');
      setImageBatches([]);
      setCurrentImageBatch(0);
      showToast('Image extraction cancelled', 'info');
      
      // Reset cancelling state after a short delay
      setTimeout(() => {
        setImageCancelling(false);
        setImageStatus('');
        setImageBatchCancelled(false);
      }, 2000);
    } catch (e) {
      showToast(e.message || 'Failed to cancel image extraction', 'error');
      setImageCancelling(false);
      setImageBatchCancelled(false);
    }
  }

  async function pollImageStatus(jobId) {
    imagePollIntervalRef.current = setInterval(async () => {
      try {
        const status = await getImageJobStatus(jobId);
        
        // Calculate progress from processed/total products
        const processed = status.processed || 0;
        const total = status.total_products || 0;
        const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;
        
        // Use API progress if available, otherwise use calculated percent
        const progressValue = status.progress != null ? Number(status.progress) : progressPercent;
        
        // Always update progress with the calculated or API value
        setImageProgress(progressValue);
        
        // Update status message with current progress
        setImageStatus(`Processing... ${processed}/${total} products (${progressPercent}%)`);
        
        if (status.status === 'completed') {
          clearInterval(imagePollIntervalRef.current);
          setExtractingImages(false);
          setImageStatus('Completed');
          await load();
          
          const success = status.successful || 0;
          const failed = status.failed || 0;
          showToast(`Image extraction completed: ${success} successful, ${failed} failed`, 'info');
          
          // Images are now auto-saved to staged_product.image_urls
          // The load() call above will refresh the data with new images from the backend
          // No need to load extracted images separately anymore
          
        } else if (status.status === 'failed') {
          clearInterval(imagePollIntervalRef.current);
          setExtractingImages(false);
          setImageStatus('Failed');
          const errorMessage = status.errors && status.errors.length > 0 
            ? status.errors.slice(0, 3).join(', ') + (status.errors.length > 3 ? '...' : '')
            : 'Unknown error';
          showToast(`Image extraction failed: ${errorMessage}`, 'error');
        }
      } catch (e) {
        console.error('Error polling image status:', e);
        clearInterval(imagePollIntervalRef.current);
        setExtractingImages(false);
        setImageStatus('Error');
        showToast('Failed to get image extraction status', 'error');
      }
    }, 2000); // Poll every 2 seconds
  }

  // Image handling functions
  const handleImageSearch = async (productId) => {
    setCurrentProductId(productId);
    setShowImageSearch(true);
    setShowImageUploader(false);
    
    // Load images for this product if not already loaded
    await loadImagesForProduct(productId);
  };

  const handleImageUpload = (productId) => {
    setCurrentProductId(productId);
    setShowImageUploader(true);
    setShowImageSearch(false);
  };

  const handleImagesSelected = (imageUrls) => {
    const updatedItem = items.find(item => item.id === currentProductId);
    if (updatedItem) {
      saveRow(currentProductId, { image_urls: imageUrls });
    }
    setShowImageSearch(false);
    setCurrentProductId(null);
  };

  const handleImageUploaded = async (response) => {
    // Backend now auto-saves to staged_product.image_urls
    // Response contains image_urls array with all images
    try {
      // Reload the row to get updated image_urls from backend
      const { items: updatedItems } = await getStagedItems(logId, { skip: page * pageSize, limit: pageSize });
      const updatedItem = updatedItems.find(item => item.id === currentProductId);
      
      if (updatedItem) {
        setItems(prevItems => 
          prevItems.map(item => 
            item.id === currentProductId ? updatedItem : item
          )
        );
      }
      
      // Notify parent component that changes have been made
      if (onChangesDetected) {
        onChangesDetected(true);
      }

      setShowImageUploader(false);
      setCurrentProductId(null);
      showToast('Custom image(s) uploaded and saved successfully', 'success');
    } catch (error) {
      console.error('Failed to reload after upload:', error);
      showToast('Image uploaded but failed to refresh', 'warning');
      setShowImageUploader(false);
      setCurrentProductId(null);
    }
  };

  const handleImageSelection = (productId, imageIndex) => {
    // Don't allow selection of already uploaded images (extracted or custom)
    const uploadedForProduct = uploadedImages[productId] || [];
    const uploadedCustomForProduct = uploadedCustomImages[productId] || [];
    
    if (uploadedForProduct.includes(imageIndex) || uploadedCustomForProduct.includes(imageIndex)) {
      return; // Do nothing if image is already uploaded
    }
    
    setSelectedImages(prev => ({
      ...prev,
      [productId]: prev[productId]?.includes(imageIndex) 
        ? prev[productId].filter(i => i !== imageIndex)
        : [...(prev[productId] || []), imageIndex]
    }));
  };

  const handleDeleteImage = async (productId, imageUrl) => {
    try {
      const response = await deleteStagedProductImage(productId, imageUrl);
      
      // Reload the row to get updated image_urls from backend
      const { items: updatedItems } = await getStagedItems(logId, { skip: page * pageSize, limit: pageSize });
      const updatedItem = updatedItems.find(item => item.id === productId);
      
      if (updatedItem) {
        setItems(prevItems => 
          prevItems.map(item => 
            item.id === productId ? updatedItem : item
          )
        );
      }
      
      // Notify parent component that changes have been made
      if (onChangesDetected) {
        onChangesDetected(true);
      }
      
      showToast('Image deleted successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to delete image', 'error');
    }
  };

  const handleImageCancel = () => {
    setShowImageSearch(false);
    setShowImageUploader(false);
    setCurrentProductId(null);
  };

  // Individual product AI handler
  const handleIndividualAI = async (productId) => {
    try {
      setIndividualAIStatus(prev => ({ ...prev, [productId]: 'running' }));
      
      const requestData = {
        allow_overwrite: allowOverwrite,
        product_ids: [productId],
      };
      
      const response = await startAIInference(logId, requestData);
      if (response.job_id) {
        // Poll for completion
        const pollAI = async () => {
          try {
            const status = await getAIJobStatus(response.job_id);
            if (status.status === 'completed') {
              setIndividualAIStatus(prev => ({ ...prev, [productId]: 'completed' }));
              // Fetch updated product data without reloading entire page
              try {
                const updatedItems = await getStagedItems(logId, { skip: page * pageSize, limit: pageSize });
                if (updatedItems.items) {
                  setItems(prev => prev.map(item => {
                    const updatedItem = updatedItems.items.find(updated => updated.id === item.id);
                    return updatedItem ? { ...item, ...updatedItem } : item;
                  }));
                }
              } catch (error) {
                console.log('Failed to fetch updated data:', error);
              }
              showToast('AI inference completed for this product', 'success');
            } else if (status.status === 'failed') {
              setIndividualAIStatus(prev => ({ ...prev, [productId]: 'failed' }));
              showToast('AI inference failed for this product', 'error');
            } else {
              // Still running, poll again
              setTimeout(pollAI, 2000);
            }
          } catch (error) {
            setIndividualAIStatus(prev => ({ ...prev, [productId]: 'failed' }));
            showToast('Failed to get AI status', 'error');
          }
        };
        pollAI();
      }
    } catch (error) {
      setIndividualAIStatus(prev => ({ ...prev, [productId]: 'failed' }));
      showToast('Failed to start AI inference', 'error');
    }
  };

  // Individual product image extraction handler - COMPLETELY SEPARATE FLOW
  const handleIndividualExtract = async (productId) => {
    try {
      setIndividualExtractStatus(prev => ({ ...prev, [productId]: 'running' }));
      
      // Use separate API for individual product extraction (auto-saves to staged_product.image_urls)
      const response = await extractImagesForProduct(productId);
      
      if (response.success) {
        setIndividualExtractStatus(prev => ({ ...prev, [productId]: 'completed' }));
        
        // Reload the row to get updated image_urls from backend
        const { items: updatedItems } = await getStagedItems(logId, { skip: page * pageSize, limit: pageSize });
        const updatedItem = updatedItems.find(item => item.id === productId);
        
        if (updatedItem) {
          setItems(prevItems => 
            prevItems.map(item => 
              item.id === productId ? updatedItem : item
            )
          );
        }
        
        // Notify parent component that changes have been made
        if (onChangesDetected) {
          onChangesDetected(true);
        }
        
        showToast(`Image extraction completed: ${response.images?.length || 0} image(s) saved`, 'success');
      } else {
        setIndividualExtractStatus(prev => ({ ...prev, [productId]: 'failed' }));
        showToast('Image extraction failed for this product', 'error');
      }
    } catch (error) {
      setIndividualExtractStatus(prev => ({ ...prev, [productId]: 'failed' }));
      showToast('Failed to start image extraction', 'error');
    }
  };

  async function runAI() {
    try {
      setAiRunning(true);
      setAiStatus('Starting AI inference...');
      setAiProgress(0);
      setAiBatchCancelled(false);
      setCurrentAiBatch(0);
      
      // Filter out products that already have complete AI data
      // Process products that are missing any AI fields (ai_low, ai_high, weight, dimensions)
      const productsWithoutAI = items.filter(item => 
        !item.ai_low || !item.ai_high || !item.weight || !item.dimensions
      );
      
      // Debug: Log AI data status for all products
      console.log('AI Data Status:', items.map(item => ({
        id: item.id,
        description: item.description,
        ai_low: item.ai_low,
        ai_high: item.ai_high,
        weight: item.weight,
        dimensions: item.dimensions,
        hasCompleteAI: !!(item.ai_low && item.ai_high && item.weight && item.dimensions)
      })));
      
      if (productsWithoutAI.length === 0) {
        showToast('All products already have AI data. No inference needed.', 'info');
        setAiRunning(false);
        return;
      }
      
      if (productsWithoutAI.length < items.length) {
        const skippedCount = items.length - productsWithoutAI.length;
        showToast(`Skipping ${skippedCount} products that already have AI data. Processing ${productsWithoutAI.length} products in batches of ${BATCH_SIZE}.`, 'info');
      }
      
      // Split products into batches of 10
      const batches = splitIntoBatches(productsWithoutAI, BATCH_SIZE);
      setAiBatches(batches);
      
      // Start processing batches sequentially
      processAIBatches(batches, 0);
      
    } catch (e) {
      showToast(`AI inference failed to start: ${e.message || 'Unknown error'}`, 'error');
      setAiRunning(false);
      setAiStatus('');
    }
  }

  async function processAIBatches(batches, batchIndex) {
    if (batchIndex >= batches.length || aiBatchCancelled) {
      // All batches completed or cancelled
      setAiRunning(false);
      if (!aiBatchCancelled) {
        setAiProgress(100);
        setAiStatus('All batches completed');
        await load(); // Reload to get updated data
        showToast(`AI Inference Complete: All ${batches.length} batch(es) processed successfully.`, 'success');
        
        // Reset after 3 seconds
        setTimeout(() => {
          setAiStatus('');
          setAiProgress(0);
          setAiBatches([]);
          setCurrentAiBatch(0);
        }, 3000);
      }
      return;
    }

    const currentBatch = batches[batchIndex];
    const totalBatches = batches.length;
    const batchNumber = batchIndex + 1;
    
    setCurrentAiBatch(batchIndex);
    setAiStatus(`Processing batch ${batchNumber}/${totalBatches} (${currentBatch.length} products)...`);
    
    // Calculate progress: (completed batches / total batches) * 100
    const progressPercent = Math.round((batchIndex / totalBatches) * 100);
    setAiProgress(progressPercent);

    try {
      const requestData = {
        allow_overwrite: allowOverwrite,
        product_ids: currentBatch.map(item => item.id),
      };
      
      // Start AI inference for this batch
      const res = await startAIInference(logId, requestData);
      const jobId = res.job_id;
      
      // Poll for this batch's completion
      await pollAIBatchUntilComplete(jobId, batchIndex, totalBatches);
      
      // Process next batch
      if (!aiBatchCancelled) {
        processAIBatches(batches, batchIndex + 1);
      }
      
    } catch (e) {
      console.error(`Error processing AI batch ${batchNumber}:`, e);
      showToast(`Batch ${batchNumber} failed: ${e.message || 'Unknown error'}`, 'error');
      
      // Continue with next batch even if current batch failed
      if (!aiBatchCancelled) {
        processAIBatches(batches, batchIndex + 1);
      }
    }
  }

  async function pollAIBatchUntilComplete(jobId, batchIndex, totalBatches) {
    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          if (aiBatchCancelled) {
            clearInterval(pollInterval);
            // Try to cancel the job
            try {
              await cancelAIJob(jobId);
            } catch (e) {
              console.error('Error cancelling AI job:', e);
            }
            reject(new Error('Batch processing cancelled'));
            return;
          }

          const status = await getAIJobStatus(jobId);
          
          // Update progress based on current batch
          const processed = status.processed || 0;
          const total = status.total_products || 0;
          const batchProgress = total > 0 ? Math.round((processed / total) * 100) : 0;
          
          // Overall progress: (completed batches + current batch progress) / total batches
          const overallProgress = Math.round(((batchIndex + (batchProgress / 100)) / totalBatches) * 100);
          setAiProgress(overallProgress);
          
          setAiStatus(`Processing batch ${batchIndex + 1}/${totalBatches}... ${processed}/${total} products (${batchProgress}%)`);

          if (status.status === 'completed') {
            clearInterval(pollInterval);
            resolve(status);
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            const errorMsg = status.errors?.join(', ') || 'Unknown error';
            console.error(`Batch ${batchIndex + 1} failed:`, errorMsg);
            reject(new Error(errorMsg));
          }
        } catch (e) {
          console.error('Error polling AI batch status:', e);
          clearInterval(pollInterval);
          reject(e);
        }
      }, 2000); // Poll every 2 seconds
    });
  }

  async function cancelAI() {
    if (aiCancelling) return; // Prevent multiple clicks
    
    try {
      setAiCancelling(true);
      setAiBatchCancelled(true);
      
      // Cancel current job if exists
      if (aiJobId) {
        try {
          await cancelAIJob(aiJobId);
        } catch (e) {
          console.error('Error cancelling AI job:', e);
        }
      }
      
      // Clear the polling interval
      if (aiPollIntervalRef.current) {
        clearInterval(aiPollIntervalRef.current);
        aiPollIntervalRef.current = null;
      }
      
      setAiRunning(false);
      setAiJobId(null);
      setAiProgress(0);
      setAiStatus('Cancelled');
      setAiBatches([]);
      setCurrentAiBatch(0);
      showToast('AI inference cancelled', 'info');
      
      // Reset cancelling state after a short delay
      setTimeout(() => {
        setAiCancelling(false);
        setAiStatus('');
        setAiBatchCancelled(false);
      }, 2000);
    } catch (e) {
      showToast(e.message || 'Failed to cancel AI inference', 'error');
      setAiCancelling(false);
      setAiBatchCancelled(false);
    }
  }

  async function pollAIStatus(jobId) {
    aiPollIntervalRef.current = setInterval(async () => {
      try {
        const status = await getAIJobStatus(jobId);
        
        // Calculate progress from processed/total products
        const processed = status.processed || 0;
        const total = status.total_products || 0;
        const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;
        
        // Use API progress if available, otherwise use calculated percent
        const progressValue = status.progress != null ? Number(status.progress) : progressPercent;
        
        // Always update progress with the calculated or API value
        setAiProgress(progressValue);
        
        // Update status message with current progress
        setAiStatus(`Processing... ${processed}/${total} products (${progressPercent}%)`);
        
        if (status.status === 'completed') {
          clearInterval(aiPollIntervalRef.current);
          setAiRunning(false);
          setAiStatus('Completed');
          await load();
          
          const success = status.successful || 0;
          const failed = status.failed || 0;
          const total = status.total_products || 0;
          let message = `AI Inference Complete: ${success}/${total} products processed successfully.`;
          if (failed > 0) {
            message += ` ${failed} failed.`;
            if (status.errors?.length > 0) message += ` Errors: ${status.errors.slice(0, 3).join(', ')}`;
          }
          showToast(message, 'info');
          
          // Reset status after 3 seconds
          setTimeout(() => {
            setAiStatus('');
            setAiProgress(0);
            setAiJobId(null);
          }, 3000);
        } else if (status.status === 'failed') {
          clearInterval(aiPollIntervalRef.current);
          setAiRunning(false);
          setAiStatus('Failed');
          showToast(`AI inference failed: ${status.errors?.join(', ') || 'Unknown error'}`, 'error');
          
          // Reset status after 3 seconds
          setTimeout(() => {
            setAiStatus('');
            setAiProgress(0);
            setAiJobId(null);
          }, 3000);
        }
      } catch (e) {
        console.error('Error polling AI status:', e);
        clearInterval(aiPollIntervalRef.current);
        setAiRunning(false);
        setAiStatus('Error checking status');
        showToast('Error checking AI inference status', 'error');
      }
    }, 2000); // Poll every 2 seconds
  }


  const handlePageChange = (newPage) => {
    if (newPage !== page && newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
    }
  };

  const isFullReplace = (log?.catalog_upload_mode || 'partial') === 'full_replace';

  return (
    <div>
      {isFullReplace && !readOnly && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Full catalog replacement
          </Typography>
          <Typography variant="body2">
            After a successful import with no failed rows, active products for this vendor that are not in this
            file will have stock set to 0 and will be hidden from the storefront (data is kept for future uploads).
          </Typography>
          {catalogImpact != null && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Current preview:{' '}
              <strong>{catalogImpact.distinct_concatenate_codes}</strong> product codes in this upload;{' '}
              <strong>{catalogImpact.products_that_would_be_zeroed}</strong> other active products for this vendor
              would be zeroed after import.
            </Typography>
          )}
        </Alert>
      )}

      {/* Pagination Header - Always visible */}
      <div style={{ 
        marginBottom: 16, 
        padding: '12px 16px',
        backgroundColor: '#f8f9fa',
        border: '2px solid #007bff',
        borderRadius: '8px',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <div>
          <b>Log #{logId}</b> — Status: <code>{log?.status}</code>
          {log?.status === 'processing' || log?.status === 'staging' ? (
            <span style={{ color: '#ff9800', marginLeft: '8px' }}>
              ⏳ Processing...
            </span>
          ) : null}
        </div>
        {total > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            padding: '8px 12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            border: '1px solid #ddd'
          }}>
            <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
              Page {page + 1} of {Math.max(1, totalPages)} ({total} total items)
              {busy && <span style={{ color: '#007bff', marginLeft: '8px' }}>Loading...</span>}
            </span>
            <span style={{ fontSize: '12px', color: '#666', backgroundColor: '#fff', padding: '2px 6px', borderRadius: '3px' }}>
              Showing {items.length} of {total} items
            </span>
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 0}
              style={{
                padding: '6px 12px',
                backgroundColor: page === 0 ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                minWidth: '80px'
              }}
            >
              ← Previous
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= Math.max(1, totalPages) - 1}
              style={{
                padding: '6px 12px',
                backgroundColor: page >= Math.max(1, totalPages) - 1 ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: page >= Math.max(1, totalPages) - 1 ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                minWidth: '80px'
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Product Filter */}
      {items.length > 0 && (
        <div style={{ 
          marginBottom: 16, 
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            marginBottom: '8px'
          }}>
            <label style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#333',
              minWidth: '80px'
            }}>
              Filter Products:
            </label>
            <select
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                minWidth: '150px'
              }}
            >
              <option value="all">All Products ({items.length})</option>
              <option value="new">New ({items.filter(item => !item.is_existing).length})</option>
              <option value="price-and-stock">Price & Stock Changed ({items.filter(item => item.is_existing && item.has_price_change && item.has_stock_change).length})</option>
              <option value="price-only">Price Only ({items.filter(item => item.is_existing && item.has_price_change && !item.has_stock_change).length})</option>
              <option value="stock-only">Stock Only ({items.filter(item => item.is_existing && item.has_stock_change && !item.has_price_change).length})</option>
              <option value="other">Other Changes ({items.filter(item => item.is_existing && item.has_changes && !item.has_price_change && !item.has_stock_change).length})</option>
              <option value="no-change">No Change ({items.filter(item => item.is_existing && !item.has_changes).length})</option>
            </select>
            <span style={{ 
              fontSize: '12px', 
              color: '#666',
              backgroundColor: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}>
              Showing {filteredItems.length} of {items.length} products
            </span>
          </div>
        </div>
      )}

      {!readOnly && !isComplete && items.length > 0 && (
        <div style={{ marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 8, backgroundColor: '#f9f9f9' }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Run Akini AI</strong>
            <p style={{ margin: '4px 0', fontSize: '14px', color: '#666' }}>
              Autofill weight and dimensions using AI inference
            </p>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={allowOverwrite}
                onChange={(e) => setAllowOverwrite(e.target.checked)}
                disabled={aiRunning}
              />
              Allow overwrite existing values
            </label>
          </div>
          <button
            onClick={runAI}
            disabled={aiRunning || busy}
            style={{
              padding: '8px 16px',
              backgroundColor: aiRunning ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: aiRunning ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {aiRunning ? 'Running AI...' : 'Run Akini AI'}
          </button>
          
          {aiRunning && (
            <button
              onClick={cancelAI}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '14px',
                marginLeft: '8px'
              }}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* AI Progress Display */}
      {aiRunning && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          border: '1px solid #4caf50', 
          borderRadius: 8, 
          backgroundColor: '#f1f8e9' 
        }}>
          <div style={{ marginBottom: 8 }}>
            <strong>AI Inference Progress</strong>
            <p style={{ margin: '4px 0', fontSize: '14px', color: '#2e7d32' }}>
              {aiStatus || 'Starting...'}
            </p>
            <p style={{ margin: '4px 0', fontSize: '12px', color: '#4caf50', fontWeight: 'bold' }}>
              {Math.max(0, Math.min(100, aiProgress))}% Complete
            </p>
          </div>
          <div style={{ 
            width: '100%', 
            backgroundColor: '#e0e0e0', 
            borderRadius: 4, 
            overflow: 'hidden',
            height: '24px',
            position: 'relative'
          }}>
            <div 
              style={{ 
                width: `${Math.max(0, Math.min(100, aiProgress))}%`, 
                height: '100%', 
                backgroundColor: '#4caf50', 
                transition: 'width 0.5s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              {aiProgress > 5 ? `${Math.round(aiProgress)}%` : ''}
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={cancelAI}
              disabled={aiCancelling}
              style={{
                padding: '6px 12px',
                backgroundColor: aiCancelling ? '#ccc' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: aiCancelling ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              {aiCancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Image Extraction Progress Display */}
      {extractingImages && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          border: '1px solid #007bff', 
          borderRadius: 8, 
          backgroundColor: '#f0f8ff' 
        }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Image Extraction Progress</strong>
            <p style={{ margin: '4px 0', fontSize: '14px', color: '#0056b3' }}>
              {imageStatus || 'Starting image extraction...'}
            </p>
            <p style={{ margin: '4px 0', fontSize: '12px', color: '#007bff', fontWeight: 'bold' }}>
              {Math.max(0, Math.min(100, imageProgress))}% Complete
            </p>
            {imageStatus && imageStatus.includes('Failed') && (
              <p style={{ margin: '4px 0', fontSize: '12px', color: '#dc3545' }}>
                Check console for detailed error information
              </p>
            )}
          </div>
          <div style={{ 
            width: '100%', 
            backgroundColor: '#e0e0e0', 
            borderRadius: 4, 
            overflow: 'hidden',
            height: '24px',
            position: 'relative'
          }}>
            <div 
              style={{ 
                width: `${Math.max(0, Math.min(100, imageProgress))}%`, 
                height: '100%', 
                backgroundColor: '#007bff', 
                transition: 'width 0.5s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              {imageProgress > 5 ? `${Math.round(imageProgress)}%` : ''}
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={cancelImageExtraction}
              disabled={imageCancelling}
              style={{
                padding: '6px 12px',
                backgroundColor: imageCancelling ? '#ccc' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: imageCancelling ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              {imageCancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {!readOnly && !isComplete && items.length > 0 && !extractingImages && (
        <div style={{ marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 8, backgroundColor: '#f9f9f9' }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Extract Images</strong>
            <p style={{ margin: '4px 0', fontSize: '14px', color: '#666' }}>
              Extract images for all products
            </p>
          </div>
          <button
            onClick={doBulkExtractImages}
            disabled={busy}
            style={{
              padding: '8px 16px',
              backgroundColor: busy ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: busy ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Extract Images
          </button>
        </div>
      )}

      {busy ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <div style={{ fontSize: '16px', marginBottom: '10px' }}>
            {log?.status === 'processing' || log?.status === 'staging' 
              ? 'Processing uploaded file... Please wait.' 
              : 'Loading items... Please wait.'
            }
          </div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            This may take a few moments for large files.
          </div>
        </div>
      ) : (
        filteredItems.map(row => (
        <RowCard
          key={row.id}
          row={row}
          readOnly={readOnly || isComplete}
          saving={savingId === row.id}
          onSave={saveRow}
          onImageSearch={handleImageSearch}
          onImageUpload={handleImageUpload}
          extractedImages={extractedImages[row.id] || []}
          selectedImages={selectedImages[row.id] || []}
          uploadedImages={uploadedImages[row.id] || []}
          uploadedCustomImages={uploadedCustomImages[row.id] || []}
          onImageSelection={handleImageSelection}
          onDeleteImage={handleDeleteImage}
          onIndividualAI={handleIndividualAI}
          onIndividualExtract={handleIndividualExtract}
          individualAIStatus={individualAIStatus[row.id] || 'idle'}
          individualExtractStatus={individualExtractStatus[row.id] || 'idle'}
        />
        ))
      )}

      {/* Bottom Pagination - Always visible for large datasets */}
      {total > pageSize && (
        <div style={{ 
          marginTop: 20, 
          padding: '12px 16px',
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '6px',
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '16px'
        }}>
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#1976d2' }}>
            Page {page + 1} of {Math.max(1, totalPages)} ({total} total items)
          </span>
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 0}
            style={{
              padding: '8px 16px',
              backgroundColor: page === 0 ? '#ccc' : '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: page === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '100px'
            }}
          >
            ← Previous
          </button>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= Math.max(1, totalPages) - 1}
            style={{
              padding: '8px 16px',
              backgroundColor: page >= Math.max(1, totalPages) - 1 ? '#ccc' : '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: page >= Math.max(1, totalPages) - 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '100px'
            }}
          >
            Next →
          </button>
        </div>
      )}

      {!readOnly && !isComplete && items.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button type="button" onClick={handleImportClick} disabled={busy}>
            {busy ? 'Importing…' : 'Import to Products'}
          </button>
        </div>
      )}

      <Dialog open={importConfirmOpen} onClose={() => !busy && setImportConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm full catalog import</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            You are importing a <strong>full catalog replacement</strong>. If the import completes with no errors,
            products for this vendor that are not in this file will be set to stock 0.
          </Typography>
          {catalogImpact != null && (
            <Typography variant="body2" color="text.secondary">
              About <strong>{catalogImpact.products_that_would_be_zeroed}</strong> active product(s) would be
              zeroed (vendor: {catalogImpact.source_vendor_slug || '—'}).
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportConfirmOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={() => runImport()} disabled={busy}>
            {busy ? 'Importing…' : 'Confirm import'}
          </Button>
        </DialogActions>
      </Dialog>

      {items.length === 0 && <div>No staged items.</div>}

      {/* Image Search Modal */}
      {showImageSearch && currentProductId && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}>
          <Box sx={{ 
            backgroundColor: 'white', 
            borderRadius: 2, 
            p: 3, 
            maxWidth: '90vw', 
            maxHeight: '90vh', 
            overflow: 'auto',
            width: '100%',
            maxWidth: 800
          }}>
            <ImageSearch
              stagedProductId={currentProductId}
              productDescription={items.find(item => item.id === currentProductId)?.description || ''}
              productCategory={items.find(item => item.id === currentProductId)?.category || ''}
              onImagesSelected={handleImagesSelected}
              onCustomUpload={() => {
                setShowImageSearch(false);
                setShowImageUploader(true);
              }}
            />
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <button onClick={handleImageCancel}>Close</button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Image Uploader Modal */}
      {showImageUploader && currentProductId && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}>
          <Box sx={{ 
            backgroundColor: 'white', 
            borderRadius: 2, 
            p: 3, 
            maxWidth: '90vw', 
            maxHeight: '90vh', 
            overflow: 'auto',
            width: '100%',
            maxWidth: 600
          }}>
            <ImageUploader
              stagedProductId={currentProductId}
              onImageUploaded={handleImageUploaded}
              onCancel={handleImageCancel}
            />
          </Box>
        </Box>
      )}
    </div>
  );
}

function RowCard({ row, readOnly, saving, onSave, onImageSearch, onImageUpload, extractedImages, selectedImages, uploadedImages, uploadedCustomImages, onImageSelection, onDeleteImage, onIndividualAI, onIndividualExtract, individualAIStatus, individualExtractStatus }) {
  const fileInputRef = useRef(null);

  const handleAvatarClick = () => {
    if (!readOnly) fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      onSave(row.id, { image_url: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const RO = (label, value) => <Field label={label} value={value ?? ''} />;
  const ED = (label, key, type = 'text') => (
    <EditableField
      label={label}
      type={type}
      value={row[key] ?? (type === 'number' ? '' : '')}
      readOnly={readOnly}
      saving={saving}
      row={row}
      fieldKey={key}
      onChange={(v) => onSave(row.id, { [key]: v })}
    />
  );

  return (
    <div style={{
      border: '1px solid #eee',
      borderRadius: 8,
      padding: 12,
      marginBottom: 10,
      background: row.status === 'failed' ? '#fff2f0' : '#fff'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <Avatar
          variant="square"
          src={row.image_url || ''}
          alt="Product"
          sx={{ width: 60, height: 60, cursor: readOnly ? 'default' : 'pointer', marginRight: 10 }}
          onClick={handleAvatarClick}
        />
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          Row {row.row_number ?? '-'} {row.error_message ? `— Error: ${row.error_message}` : ''}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {ED('MFR', 'brand')}
        {ED('Description', 'description')}
        {ED('Location', 'location')}
        {RO('Category', row.category || 'Uncategorized')}

        {ED('Condition', 'condition')}
        {ED('Qty', 'stock_quantity', 'number')}
        {ED('CC', 'concatenate_code')}
        {ED('Part # (SKU)', 'sku')}

        {RO('Vendor', row.vendor ? row.vendor.toUpperCase() : 'Not specified')}

        {ED('APDI $ (Cost)', 'cost_price', 'number')}
        {ED('APDI $ (Retail)', 'retail_price', 'number')}
        {ED('Weight', 'weight', 'number')}
        {ED('Dimensions', 'dimensions')}

        {/* Vendor-specific fields */}
        {/* MFP Technology: SUB P/N, SUB 2 P/N, and Long Description (specifications) */}
        {(row.vendor?.toLowerCase() === 'mfp-technology' || row.vendor?.toLowerCase() === 'mfp technology') && (
          <>
            {ED('SUB P/N', 'sub_part_number')}
            {ED('SUB 2 P/N', 'sub_2_part_number')}
            {ED('Long Description', 'specifications')}
          </>
        )}
        
        {/* Sourcing IT: Long Description (specifications) */}
        {(row.vendor?.toLowerCase() === 'sourcing-it' || row.vendor?.toLowerCase() === 'sourcing it') && (
          <>
            {ED('Long Description', 'specifications')}
          </>
        )}
        
        {/* Lenovo Top Choice: Long Description (specifications) */}
        {(row.vendor?.toLowerCase() === 'lenovo-top-choice' || row.vendor?.toLowerCase() === 'lenovo top choice' || row.vendor?.toLowerCase() === 'lenovo') && (
          <>
            {ED('Long Description', 'specifications')}
          </>
        )}
        
        {/* LCR: Long Description (specifications) */}
        {(row.vendor?.toLowerCase() === 'lcr') && (
          <>
            {ED('Long Description', 'specifications')}
          </>
        )}

        {RO('AI Low : $', row.ai_low)}
        {RO('AI High: $', row.ai_high)}
      </div>

      {/* Individual Action Buttons */}
      <div style={{ marginTop: 12, padding: 8, backgroundColor: '#f8f9fa', borderRadius: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => onIndividualAI(row.id)}
          disabled={readOnly || individualAIStatus === 'running'}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            backgroundColor: individualAIStatus === 'running' ? '#ccc' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: individualAIStatus === 'running' ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          {individualAIStatus === 'running' ? 'Running AI...' : 'Run Akini AI'}
        </button>
        
        <button
          onClick={() => onIndividualExtract(row.id)}
          disabled={readOnly || individualExtractStatus === 'running'}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            backgroundColor: individualExtractStatus === 'running' ? '#ccc' : '#4caf50',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: individualExtractStatus === 'running' ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          {individualExtractStatus === 'running' ? 'Extracting...' : 'Extract Images'}
        </button>
      </div>

      {/* Image Section */}
      <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
          Product Images
        </Typography>
        
        {/* Display images from staged_product.image_urls (auto-saved from extraction/upload) */}
        {row.image_urls && row.image_urls.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Product Images ({row.image_urls.length} image(s)):
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {row.image_urls.map((url, index) => (
                <Card 
                  key={index} 
                  sx={{ 
                    width: 160, 
                    height: 160, 
                    border: '1px solid #ddd',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={url}
                    alt={`Product image ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  {/* Cross button to delete image - top left */}
                  {!readOnly && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this image?')) {
                          onDeleteImage(row.id, url);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#d32f2f';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f44336';
                      }}
                    >
                      ×
                    </button>
                  )}
                </Card>
              ))}
            </Box>
          </Box>
        ) : (
          /* No images available */
          <Box sx={{ mb: 2, textAlign: 'center', py: 2 }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              No images available. Upload custom image or extract images.
            </Typography>
          </Box>
        )}

        {/* Action buttons */}
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <button
              onClick={() => onImageUpload(row.id)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Upload Custom Image
            </button>
          </Box>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{label}</div>
      <div style={{ padding: '6px 8px', fontSize: 14 }}>{String(value ?? '')}</div>
    </div>
  );
}

function EditableField({ label, value, onChange, readOnly, type = 'text', saving, row, fieldKey }) {
  const [draft, setDraft] = useState(value ?? '');
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  return (
    <div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{label}</div>
      <input
        style={{
          width: '100%',
          padding: '6px 8px',
          border: error ? '1px solid red' : '1px solid #ddd',
          borderRadius: 6
        }}
        value={draft}
        type={type}
        readOnly={readOnly}
        onChange={(e) => {
          if (readOnly) return;
          setDraft(e.target.value);
          setError('');
        }}
        onBlur={() => {
          if (readOnly) return;
          let v = draft;
          if (type === 'number') {
            if (v === '' || v === null) {
              v = null;
            } else {
              const parsed = Number(v);
              if (Number.isNaN(parsed)) {
                setError('Please enter a valid number');
                return;
              }
              v = parsed;
            }
          }
          if (fieldKey === 'retail_price') {
            const cost = row?.cost_price == null ? null : Number(row.cost_price);
            if (cost != null && v != null && v <= cost) {
              setError('Retail price must be greater than cost price');
              return;
            }
          }
          onChange(v);
        }}
      />
      {error && <div style={{ fontSize: 11, color: 'red', marginTop: 4 }}>{error}</div>}
      {saving && <div style={{ fontSize: 11, color: '#888' }}>Saving…</div>}
    </div>
  );
}